import React, { useEffect, useState } from 'react';
// Force rebuild
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Calendar from '../components/Calendar';
import CalendarSyncModal from '../components/CalendarSyncModal';
import { useAuth } from '../contexts/AuthContext';
import { useActiveProject } from '../hooks/useActiveProject';
import { supabase } from '../lib/supabase';
import { syncAllCalendars } from '../services/calendarIntegration';
import '../styles/MeetingPlan.css';
import '../styles/Pages.css';
import Toast from '../components/Toast';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color?: string;
  description?: string;
}

const MeetingPlan: React.FC = () => {
  const { user } = useAuth();
  const { activeProjectId } = useActiveProject();
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    document.title = 'Meeting Plan | Personal Workspace';
  }, []);

  useEffect(() => {
    if (user) {
      loadEvents();
    }
  }, [user, activeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('meeting_plans')
        .select('*')
        .eq('user_id', user.id);

      if (activeProjectId) query = query.eq('project_id', activeProjectId);

      const { data, error } = await query.order('start_time', { ascending: true });

      if (error) throw error;

      if (data) {
        const loadedEvents: CalendarEvent[] = data.map((event: any) => ({
          id: event.id,
          title: event.title,
          start: new Date(event.start_time),
          end: new Date(event.end_time),
          color: event.provider === 'google' ? '#4285f4' : event.provider === 'microsoft' ? '#0078d4' : '#3b82f6',
          description: event.description
        }));

        if (loadedEvents.length > 0) {
          setEvents(loadedEvents);
        } else {
          setEvents([]);
        }
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const handleSyncAll = async () => {
    if (!user) return;

    setIsSyncing(true);
    try {
      const result = await syncAllCalendars(user.id);
      if (result.success) {
        // Reload events
        await loadEvents();
        setToast({ message: `Synced ${result.successCount} calendars successfully`, type: 'success' });
      } else {
        console.error(`Failed to sync ${result.failureCount} calendars`);
        setToast({ message: `Failed to sync ${result.failureCount} calendars`, type: 'error' });
      }
    } catch (error) {
      console.error('Error syncing calendars:', error);
      setToast({ message: 'Error syncing calendars', type: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleEventClick = (event: CalendarEvent) => {
    navigate(`/meeting/${event.id}`);
  };

  const handleDateClick = (date: Date) => {
    console.log('Date clicked:', date);
    // TODO: Open create event modal
  };

  const handleCreateEvent = (start: Date, end: Date) => {
    console.log('Create event:', { start, end });
    // TODO: Open create event modal with pre-filled times
  };

  const handleEventsImported = () => {
    // Reload events after import
    loadEvents();
    setIsSyncModalOpen(false);
  };

  return (
    <>
      <Layout>
        <div className="meeting-plan-container fade-in">
          <div className="meeting-plan-header-row">
            <div className="page-breadcrumb">
              <div className="page-breadcrumb-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </div>
              <h1 className="page-breadcrumb-title">Meeting Plans</h1>
            </div>
            <div className="header-actions">
              <button
                className="sync-btn"
                onClick={handleSyncAll}
                disabled={isSyncing}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={isSyncing ? 'spin' : ''}
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
                </svg>
                {isSyncing ? 'Syncing...' : 'Sync'}
              </button>
              <button className="import-btn btn-primary-cta" onClick={() => setIsSyncModalOpen(true)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                Import Calendar
              </button>
            </div>
          </div>
          <Calendar
            events={events}
            onEventClick={handleEventClick}
            onDateClick={handleDateClick}
            onCreateEvent={handleCreateEvent}
          />
        </div>
      </Layout>

      <CalendarSyncModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        onEventsImported={handleEventsImported}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default MeetingPlan;
